import { css } from "@emotion/react"
import styled from "@emotion/styled"
import { PTabsBar } from "@porsche-design-system/components-react"
import type { PTabsBarProps } from "@porsche-design-system/components-react"
import {
	colorCanvas,
	colorFrosted,
	colorSurface,
	leadingNormal,
	radiusMd,
	radiusLg,
	radiusXl,
	spacingStaticSm,
	spacingStaticXs,
} from "@porsche-design-system/components-react/emotion"
import type { PropsWithChildren } from "react"
import { shouldForwardProp } from "src/common/shouldForwardProp"
import { SkeletonComponent } from "./SkeletonComponent"

interface SkeletonTabsBarProps extends PropsWithChildren {
	background?: Exclude<PTabsBarProps["background"], "none">
	buttonCount?: number
	className?: string
	compact?: boolean
}

export const SkeletonTabsBar = ({ background, buttonCount, children, className, compact }: SkeletonTabsBarProps) => {
	return children ? (
		// combination of real link or button tabs with skeleton tabs
		<PTabsBar className={className} background={background} compact={compact} activeTabIndex={0}>
			{children}
			{Array.from({ length: buttonCount ?? 0 }, (_, i) => (
				<Pill key={i} $width="100px" $compact={compact} $hasBackground={!!background} />
			))}
		</PTabsBar>
	) : buttonCount ? (
		<Wrapper className={className} $background={background} $compact={compact}>
			{Array.from({ length: buttonCount }, (_, i) => (
				<Pill key={i} $width="100px" $compact={compact} $hasBackground={!!background} />
			))}
		</Wrapper>
	) : background ? (
		<Wrapper className={className} $background={background} $compact={compact} $wrapsSinglePill>
			<Pill $width="100%" $compact={compact} $hasBackground={!!background} />
		</Wrapper>
	) : (
		<Pill className={className} $compact={compact} />
	)
}

const backgroundColorMap: Record<NonNullable<SkeletonTabsBarProps["background"]>, string> = {
	canvas: colorCanvas,
	frosted: colorFrosted,
	surface: colorSurface,
}

const Pill = styled(SkeletonComponent)<{
	$compact?: boolean
	$hasBackground?: boolean
}>`
	height: ${leadingNormal};
	padding: ${({ $compact }) => ($compact ? "6px 0" : "16px 0")};
	border-radius: ${({ $compact }) => ($compact ? radiusLg : radiusXl)};
	max-width: 320px;
	${({ $compact, $hasBackground }) =>
		$hasBackground &&
		css`
			padding: ${$compact ? "3px 0" : "12px 0"};
			border-radius: ${$compact ? radiusMd : radiusLg};
		`}}
`

const Wrapper = styled("div", { shouldForwardProp })<{
	$background: SkeletonTabsBarProps["background"]
	$compact?: boolean
	$wrapsSinglePill?: boolean
}>`
	display: flex;
	flex-direction: row;
	gap: ${spacingStaticSm};
	justify-self: start;
	${({ $background, $compact }) =>
		$background &&
		css`
			background: ${backgroundColorMap[$background]};
			border-radius: ${$compact ? radiusLg : radiusXl};
			padding: ${$compact ? "3px" : spacingStaticXs};
		`}
	${({ $wrapsSinglePill }) =>
		$wrapsSinglePill &&
		css`
			width: 100%;
			max-width: 320px;
		`}
`
